import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Wallet } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { FormSelect } from '@/components/ui/select'
import { Field, FormSection } from '../../components/FormBits'
import { toastError } from '../../ui'
import { useSaveWallet } from '../api'
import { WALLET_TYPES, type PersonalWallet } from '../types'

export function WalletForm({ wallet, onDone }: { wallet: PersonalWallet | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveWallet()
  const schema = z.object({
    name: z.string().trim().min(2, t('finance.accounts.form.nameRequired')).max(60),
    type: z.enum(WALLET_TYPES),
    bankName: z.string().trim().max(60),
    last4: z.string().trim().regex(/^(\d{4})?$/, t('errors.CARD_LAST4_INVALID')),
    opening: z.string().regex(/^\d+(\.\d{1,2})?$/, t('finance.form.amountInvalid')),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { name: wallet?.name ?? '', type: wallet?.type ?? 'bank', bankName: wallet?.bankName ?? '', last4: wallet?.last4 ?? '', opening: wallet?.opening ?? '0.00' } })
  const { errors } = form.formState
  const type = useWatch({ control: form.control, name: 'type' })
  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await save.mutateAsync({ id: wallet?.id, input })
      toast.success(t('common.saved'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })
  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Wallet />}
      title={t(wallet ? 'finance.personal.form.editWallet' : 'finance.personal.form.createWallet')}
      description={t('finance.personal.privateHint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="pw-name" label={t('finance.accounts.name')} error={errors.name?.message} full>
          <Input id="pw-name" aria-invalid={!!errors.name} {...form.register('name')} />
        </Field>
        <Field id="pw-type" label={t('finance.accounts.type')}>
          <FormSelect id="pw-type" control={form.control} name="type" options={WALLET_TYPES.map((x) => ({ value: x, label: t(`finance.personal.walletType.${x}`) }))} />
        </Field>
        <Field id="pw-open" label={t('finance.accounts.opening')} error={errors.opening?.message}>
          <Controller control={form.control} name="opening" render={({ field }) => <NumberInput id="pw-open" value={field.value} onChange={field.onChange} onBlur={field.onBlur} minDecimalsOnBlur={2} suffix="₺" />} />
        </Field>
        {type !== 'cash' ? (
          <>
            <Field id="pw-bank" label={t('finance.accounts.bank')}>
              <Input id="pw-bank" {...form.register('bankName')} />
            </Field>
            <Field id="pw-last4" label={t('finance.accounts.cardLast4')} error={errors.last4?.message} hint={t('finance.accounts.form.cardSafety')}>
              <Input id="pw-last4" inputMode="numeric" maxLength={4} autoComplete="off" {...form.register('last4')} />
            </Field>
          </>
        ) : null}
      </FormSection>
    </Modal>
  )
}
